import axios, { AxiosError, AxiosInstance, AxiosInterceptorManager, AxiosRequestConfig, AxiosResponse } from 'axios';

import { store } from '../index';
import { getAuthorizationHeader } from '../cores/getAuthorizationHeader';
import { getLocalStorageRefreshToken, setLocalStorageRefreshToken, setLocalStorageToken } from '../sagas/authentication';
import { ToastActions, ToastMessageTypes } from '../store/toast';

interface PxiosResponseData<T = any> extends AxiosResponse<T> {
  toast?: { message: string; type: ToastMessageTypes };
}

interface PxiosInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<AxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<PxiosResponseData>>;
  };
}

const plainAxios = axios.create({
  baseURL: `${process.env.REACT_APP_HOST}/kcar`,
  headers: getAuthorizationHeader()
});

let refreshTokenPromise: Promise<unknown> | null = null;

const pxios: PxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_HOST}/kcar`,
  headers: getAuthorizationHeader()
});
pxios.interceptors.response.use(undefined, async (error: AxiosError) => {
  if (error.response?.status !== 401 || !error.config) {
    throw error;
  }

  if (!refreshTokenPromise) {
    refreshTokenPromise = new Promise((resolve, reject) => {
      const refreshToken = getLocalStorageRefreshToken();
      if (!refreshToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.reload();
        reject();
        return;
      }

      plainAxios
        .post('/jwt/refresh/', { refresh: refreshToken })
        .then(({ data: { token, refresh_token } }: { data: { token: string; refresh_token: string } }) => {
          setLocalStorageToken(token);
          setLocalStorageRefreshToken(refresh_token);
          resolve(undefined);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.reload();
          reject();
        });
    }).finally(() => {
      refreshTokenPromise = null;
    });
  }

  await refreshTokenPromise;

  const authorization = getAuthorizationHeader().Authorization || '';
  pxios.defaults.headers['Authorization'] = authorization;
  error.config.headers.Authorization = authorization;

  return pxios(error.config);
});
pxios.interceptors.response.use(
  response => {
    if (response.data.toast) {
      const { message, type } = response.data.toast;
      store.dispatch(ToastActions.createToast({ type, message }));
    }

    return response;
  },
  err => {
    if (err.response?.data?.toast) {
      const { message, type } = err.response.data.toast;
      store.dispatch(ToastActions.createToast({ type, message }));
    }
    throw new Error(err.response.data.toast.message);
  }
);

export const setAuthorizedRequesterToken = (token: string) => {
  pxios.defaults.headers['Authorization'] = `JWT ${token}`;
};

export default pxios;
